import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { Box, Flex, HStack } from '@televet/kibble-ui/build/chakra';
import { GetClinicDirectBookingSettingsQuery, TimeUnit } from 'shared/types/graphql';
import { UpdateDirectBookingSettingsReturnType } from './UpdateDirectBookingSettingsReturnType';
import * as Sentry from '@sentry/react';
import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import { Select, SelectOptionProps } from '@televet/kibble-ui/build/components/Select';
import { Tooltip } from '@televet/kibble-ui/build/components/Tooltip';
import { Heading } from '@televet/kibble-ui/build/components/Heading';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { Text } from '@televet/kibble-ui/build/components/Text';
import debounce from 'lodash-es/debounce';

const allowedMax = (unit: TimeUnit): number => {
  if (unit === TimeUnit.Months) return 3;
  if (unit === TimeUnit.Weeks) return 13;
  if (unit === TimeUnit.Days) return 90;
  return 0;
};

// Dropdown options: only days, weeks, months.
const unitOptions: SelectOptionProps[] = [
  { label: 'Day(s)', value: TimeUnit.Days },
  { label: 'Week(s)', value: TimeUnit.Weeks },
  { label: 'Month(s)', value: TimeUnit.Months },
];

export type AdvanceBookingLimitationsSectionInput = {
  directBookingSettingsData?: GetClinicDirectBookingSettingsQuery;
  updateDirectBookingSettings: UpdateDirectBookingSettingsReturnType;
  refetchSettings: () => void;
  clinicId: string | undefined;
};

export function AdvanceBookingLimitationsSection({
  directBookingSettingsData,
  updateDirectBookingSettings,
  clinicId,
  refetchSettings,
}: AdvanceBookingLimitationsSectionInput): JSX.Element {
  const initialValue = directBookingSettingsData?.findFirstClinicDirectBookingSetting?.maxTimeInAdvanceValue ?? 3;
  const initialUnit =
    directBookingSettingsData?.findFirstClinicDirectBookingSetting?.maxTimeInAdvanceUnit ?? TimeUnit.Months;

  const [maxTimeInput, setMaxTimeInput] = useState<number>(initialValue);
  const [timeUnit, setTimeUnit] = useState<TimeUnit>(initialUnit);
  const [errorText, setErrorText] = useState<string | null>(null);

  useEffect(() => {
    setMaxTimeInput(initialValue);
    setTimeUnit(initialUnit);
  }, [initialValue, initialUnit]);

  const updateSettings = useCallback(
    (maxTimeInput, timeUnit, errorText): void => {
      if (errorText) return;
      if (!clinicId) return;

      const settings = directBookingSettingsData?.findFirstClinicDirectBookingSetting;
      if (settings?.maxTimeInAdvanceValue === maxTimeInput && settings?.maxTimeInAdvanceUnit === timeUnit) {
        return;
      }

      updateDirectBookingSettings({
        variables: {
          where: { id: settings?.id || '' },
          update: {
            maxTimeInAdvanceValue: maxTimeInput,
            maxTimeInAdvanceUnit: timeUnit,
          },
          create: {
            maxTimeInAdvanceValue: maxTimeInput,
            maxTimeInAdvanceUnit: timeUnit,
            clinic: {
              connect: { id: clinicId },
            },
          },
        },
      })
        .catch((err) => {
          console.error(err);
          Sentry.captureException(err);
        })
        .finally(() => {
          refetchSettings();
        });
    },
    [clinicId, directBookingSettingsData, updateDirectBookingSettings, refetchSettings],
  );

  const triggerUpdateDebounced = useMemo(() => {
    return debounce((maxTimeInput, timeUnit, errorText) => {
      updateSettings(maxTimeInput, timeUnit, errorText);
    }, 1000);
  }, [updateSettings]);

  const handleMaxTimeInputChange = useCallback(
    (value: string): void => {
      const num = Number(value);
      let errorText: string | null = null;
      if (num > allowedMax(timeUnit)) {
        errorText = `Maximum allowed is ${allowedMax(timeUnit)} ${timeUnit}`;
      } else if (num < 1) {
        errorText = `Minimum allowed is 1`;
      }
      setErrorText(errorText);
      setMaxTimeInput(num);
      triggerUpdateDebounced(num, timeUnit, errorText);
    },
    [timeUnit, triggerUpdateDebounced],
  );

  const handleUnitChange = useCallback(
    (option: SelectOptionProps): void => {
      const newUnit = option.value as TimeUnit;
      let errorText: string | null = null;
      if (maxTimeInput > allowedMax(newUnit)) {
        errorText = `Maximum allowed is ${allowedMax(newUnit)} ${newUnit}`;
      }
      setErrorText(errorText);
      setTimeUnit(newUnit);
      triggerUpdateDebounced(maxTimeInput, newUnit, errorText);
    },
    [maxTimeInput, triggerUpdateDebounced],
  );

  return (
    <Flex mt={5} direction="column" maxW="550px" mb={5}>
      <Heading size="md">Advance Booking Limitations</Heading>
      <Box mt={5} mb={5}>
        <Text size="sm" variant="subtle">
          Control how far in advance clients can schedule appointments. The default time frame is 3 months in advance
          for any appointment turned on with direct booking.
        </Text>
      </Box>
      <HStack justifyContent="flex-start" alignItems="center" mb={1} spacing={1}>
        <Text size="md" fontWeight="bold">
          How far in advance can clients book?
        </Text>
        <Tooltip label="This is the farthest out that a client will be able to book an appointment through a Direct Booking request">
          <Flex>
            <Icon name="questionMarkCircleSolid" size="sm" variant="subtle" />
          </Flex>
        </Tooltip>
      </HStack>
      <HStack justifyContent="flex-start" alignItems="center">
        <Box maxW="60px">
          <TextInput
            inputMode="numeric"
            placeholder="0"
            value={maxTimeInput}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => handleMaxTimeInputChange(e.target.value)}
          />
        </Box>
        <Select
          label=""
          maxW="240px"
          options={unitOptions}
          size="md"
          value={timeUnit}
          listProps={{ onSelect: handleUnitChange }}
        />
      </HStack>
      {errorText && (
        <Box mt={2}>
          <Text size="sm" variant="danger">
            {errorText}
          </Text>
        </Box>
      )}
    </Flex>
  );
}
